/**
 * Страница входа клиента (Figma Potok / node 20:4514).
 */

import { useState, type FormEvent } from "react";
import type { ClientAccessResult } from "@potok/shared";
import type { PublicCatalogApi } from "../../api/catalog.api";
import { ApiRequestError } from "../../api/http";
import { SmartCaptchaField } from "../captcha/SmartCaptchaField";

type LoginMode = "code" | "email";

type ClientLoginPageProps = {
  api: PublicCatalogApi;
  onAccessSuccess: (result: ClientAccessResult) => void;
  onGoRegister?: () => void;
  onGoWizard?: () => void;
  adminAppUrl?: string;
};

export function ClientLoginPage({
  api,
  onAccessSuccess,
  onGoRegister,
  onGoWizard,
  adminAppUrl = "http://localhost:5174",
}: ClientLoginPageProps) {
  const [mode, setMode] = useState<LoginMode>("code");
  const [personalCode, setPersonalCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const access =
        mode === "code"
          ? await api.accessClient({
              personalCode: personalCode.trim(),
              captchaToken: captchaToken || undefined,
            })
          : await api.loginClient({
              email: email.trim(),
              password,
              captchaToken: captchaToken || undefined,
            });
      onAccessSuccess(access);
    } catch (caught: unknown) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-page__card">
        <div className="login-page__logo" aria-hidden="true">
          <span className="login-page__mark" />
          <div className="login-page__brand-text">
            <p className="login-page__brand">ПОТОК</p>
            <p className="login-page__tagline">КМЗКО · konmash.ru</p>
          </div>
        </div>

        <h1 className="login-page__title">Вход в личный кабинет</h1>
        <p className="login-page__subtitle">Система подбора конвейерного оборудования</p>

        <form className="login-page__form" onSubmit={(event) => void submit(event)}>
          {error ? (
            <p className="login-page__error" role="alert">
              {error}
            </p>
          ) : null}

          <div className="login-page__tabs" role="tablist" aria-label="Способ входа">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "code"}
              className={mode === "code" ? "login-page__tab login-page__tab--active" : "login-page__tab"}
              onClick={() => setMode("code")}
              disabled={busy}
            >
              По коду доступа
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "email"}
              className={mode === "email" ? "login-page__tab login-page__tab--active" : "login-page__tab"}
              onClick={() => setMode("email")}
              disabled={busy}
            >
              По e-mail
            </button>
          </div>

          {mode === "code" ? (
            <div className="login-page__field">
              <label className="login-page__label" htmlFor="client-login-code">
                Персональный код <span className="login-page__req">*</span>
              </label>
              <input
                id="client-login-code"
                className="login-page__code-input"
                value={personalCode}
                onChange={(event) => setPersonalCode(event.target.value.replace(/\D/g, "").slice(0, 8))}
                inputMode="numeric"
                pattern="[0-9]{8}"
                minLength={8}
                maxLength={8}
                placeholder="XXXXXXXX"
                required
                disabled={busy}
                autoComplete="one-time-code"
              />
              <p className="login-page__hint">
                Персональный код выдаётся при регистрации на сайте или менеджером КМЗКО
              </p>
            </div>
          ) : (
            <>
              <div className="login-page__field">
                <label className="login-page__label" htmlFor="client-login-email">
                  E-mail <span className="login-page__req">*</span>
                </label>
                <input
                  id="client-login-email"
                  className="login-page__input"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="name@company.ru"
                  required
                  disabled={busy}
                  autoComplete="username"
                />
              </div>
              <div className="login-page__field">
                <label className="login-page__label" htmlFor="client-login-password">
                  Пароль <span className="login-page__req">*</span>
                </label>
                <input
                  id="client-login-password"
                  className="login-page__input"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••••••"
                  required
                  minLength={12}
                  disabled={busy}
                  autoComplete="current-password"
                />
              </div>
            </>
          )}

          <SmartCaptchaField onToken={setCaptchaToken} disabled={busy} />

          <button type="submit" className="login-page__submit" disabled={busy}>
            Войти
          </button>

          <p className="login-page__register">
            Нет кабинета?{" "}
            <button type="button" className="login-page__link" onClick={onGoRegister} disabled={busy}>
              Зарегистрироваться
            </button>
          </p>

          <div className="login-page__divider">
            <a className="login-page__admin-link" href={adminAppUrl}>
              Вход для администраторов →
            </a>
          </div>
        </form>
      </div>

      {onGoWizard ? (
        <button type="button" className="login-page__back" onClick={onGoWizard}>
          ← Вернуться к подбору оборудования
        </button>
      ) : null}
    </div>
  );
}

function describeError(caught: unknown): string {
  if (caught instanceof ApiRequestError) {
    return caught.message;
  }
  return "Не удалось войти. Проверьте данные и попробуйте снова.";
}
